/**
 * ViewCreatePage - Multi-step wizard for creating views
 *
 * Provides a guided 6-step process for creating data views with rule-based
 * file selection, column configuration, transformations, and refresh settings.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/useToast';
import { useCreateView } from '@/hooks/useViews';
import type { CreateViewRequest, ViewRule, ViewColumn } from '@/types/views';
import { ViewWizardSteps } from '@/components/views/wizard/ViewWizardSteps';
import { BasicInfoStep } from '@/components/views/wizard/BasicInfoStep';
import { RuleBuilderStep } from '@/components/views/wizard/RuleBuilderStep';
import { ColumnConfigStep } from '@/components/views/wizard/ColumnConfigStep';
import { TransformationsStep } from '@/components/views/wizard/TransformationsStep';
import { RefreshSettingsStep } from '@/components/views/wizard/RefreshSettingsStep';
import { ReviewStep } from '@/components/views/wizard/ReviewStep';

const TOTAL_STEPS = 6;

const STEP_TITLES = [
  'Basic Information',
  'File Selection Rules',
  'Advanced Transformations',
  'Column Configuration',
  'Refresh Settings',
  'Review & Create',
];

const STEP_DESCRIPTIONS = [
  'Provide a name and description for your view',
  'Define which files should be included in this view',
  'Add optional WHERE, GROUP BY, and JOIN transformations',
  'Select which columns to include in the view',
  'Configure how and when this view refreshes',
  'Review your configuration and create the view',
];

/**
 * Form data interface for the view creation wizard
 */
export interface ViewFormData {
  // Step 1: Basic Info
  name: string;
  description: string;
  tags: string[];

  // Step 2: Rules
  rules: ViewRule[];
  rule_logic: 'AND' | 'OR';

  // Step 3: Transformations
  where_conditions: any[];
  group_by: any | null;
  having_conditions: any[];
  joins: any[];

  // Step 4: Columns
  select_all_columns: boolean;
  columns: ViewColumn[];

  // Step 5: Refresh Settings
  is_auto_refresh: boolean;
  refresh_schedule: 'on_ingestion' | 'hourly' | 'daily' | 'weekly' | 'manual';
  view_type: 'virtual' | 'materialized';
  partition_columns?: string[];
}

/**
 * Initial form state
 */
const initialFormData: ViewFormData = {
  name: '',
  description: '',
  tags: [],
  rules: [],
  rule_logic: 'AND',
  select_all_columns: true,
  columns: [],
  where_conditions: [],
  group_by: null,
  having_conditions: [],
  joins: [],
  is_auto_refresh: true,
  refresh_schedule: 'on_ingestion',
  view_type: 'virtual',
};

/**
 * ViewCreatePage Component
 */
export const ViewCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const createView = useCreateView();

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<ViewFormData>(initialFormData);

  /**
   * Handle cancel - navigate back to views list
   */
  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? All progress will be lost.')) {
      navigate('/views');
    }
  };

  /**
   * Update form data
   */
  const updateFormData = (updates: Partial<ViewFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  /**
   * Validate current step before proceeding
   */
  const validateStep = (step: number): { isValid: boolean; message?: string } => {
    switch (step) {
      case 1: {
        // Basic info: name is required
        if (!formData.name.trim()) {
          return { isValid: false, message: 'View name is required' };
        }
        if (formData.name.length < 3 || formData.name.length > 100) {
          return { isValid: false, message: 'View name must be between 3 and 100 characters' };
        }
        if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(formData.name)) {
          return {
            isValid: false,
            message: 'View name must start with a letter and contain only letters, numbers, and underscores (no hyphens or spaces)',
          };
        }
        return { isValid: true };
      }
      case 2: {
        // Rules: at least one rule required
        if (formData.rules.length === 0) {
          return { isValid: false, message: 'At least one file selection rule is required' };
        }
        return { isValid: true };
      }
      case 3: {
        // Transformations: optional, always valid
        return { isValid: true };
      }
      case 4: {
        // Columns: if not select_all, must have at least one column
        if (!formData.select_all_columns && formData.columns.length === 0) {
          return { isValid: false, message: 'At least one column must be selected' };
        }
        return { isValid: true };
      }
      case 5: {
        // Refresh settings: always valid (has defaults)
        return { isValid: true };
      }
      case 6: {
        // Review: validate entire form
        const step1 = validateStep(1);
        if (!step1.isValid) return step1;
        const step2 = validateStep(2);
        if (!step2.isValid) return step2;
        const step3 = validateStep(3);
        if (!step3.isValid) return step3;
        const step4 = validateStep(4);
        if (!step4.isValid) return step4;
        return { isValid: true };
      }
      default:
        return { isValid: false };
    }
  };

  /**
   * Handle next step
   */
  const handleNext = () => {
    const validation = validateStep(currentStep);
    if (!validation.isValid) {
      toast({
        title: 'Validation Error',
        description: validation.message,
        variant: 'destructive',
      });
      return;
    }
    setCurrentStep((prev) => Math.min(prev + 1, TOTAL_STEPS));
  };

  /**
   * Handle previous step
   */
  const handlePrevious = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  /**
   * Handle create view submission
   */
  const handleCreate = async () => {
    const validation = validateStep(6);
    if (!validation.isValid) {
      toast({
        title: 'Validation Error',
        description: validation.message,
        variant: 'destructive',
      });
      return;
    }

    try {
      // Build create request with correct backend field names
      const request: CreateViewRequest = {
        name: formData.name,
        description: formData.description || undefined,
        tags: formData.tags.length > 0 ? formData.tags : undefined,
        view_type: formData.view_type,
        rules: formData.rules,
        rule_logic: formData.rule_logic,
        select_all_columns: formData.select_all_columns,
        columns: formData.select_all_columns ? [] : formData.columns,
        where_conditions: formData.where_conditions.length > 0 ? formData.where_conditions : undefined,
        group_by: formData.group_by || undefined,
        having_conditions: formData.having_conditions.length > 0 ? formData.having_conditions : undefined,
        joins: formData.joins.length > 0 ? formData.joins : undefined,
        is_auto_refresh: formData.is_auto_refresh,
        refresh_schedule: formData.refresh_schedule,
        partition_columns: formData.partition_columns,
      };

      const newView = await createView.mutateAsync(request);
      toast({
        title: 'View Created',
        description: `View "${newView.name}" has been created successfully.`,
      });
      navigate(`/views/${newView.id}`);
    } catch (error: any) {
      toast({
        title: 'Failed to Create View',
        description: error?.response?.data?.detail || error?.response?.data?.message || error.message || 'An unexpected error occurred',
        variant: 'destructive',
      });
    }
  };

  /**
   * Render current step component
   */
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <BasicInfoStep formData={formData} updateFormData={updateFormData} />;
      case 2:
        return (
          <RuleBuilderStep
            formData={formData}
            updateFormData={updateFormData}
            excludeViewNames={formData.name.trim() ? [formData.name.trim()] : undefined}
          />
        );
      case 3:
        return <TransformationsStep formData={formData} updateFormData={updateFormData} currentViewId={undefined} />;
      case 4:
        return <ColumnConfigStep formData={formData} updateFormData={updateFormData} />;
      case 5:
        return <RefreshSettingsStep formData={formData} updateFormData={updateFormData} />;
      case 6:
        return <ReviewStep formData={formData} onEdit={setCurrentStep} onCreate={handleCreate} isLoading={createView.isPending} />;
      default:
        return null;
    }
  };

  const progress = (currentStep / TOTAL_STEPS) * 100;

  return (
    <div className="container mx-auto py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">Create New View</h1>
          <p className="text-muted-foreground mt-1">
            Step {currentStep} of {TOTAL_STEPS}: {STEP_TITLES[currentStep - 1]}
          </p>
        </div>
        <Button variant="ghost" onClick={handleCancel}>
          <X className="h-4 w-4 mr-2" />
          Cancel
        </Button>
      </div>

      {/* Progress Bar */}
      <div className="mb-8">
        <Progress value={progress} className="h-2" />
        <ViewWizardSteps currentStep={currentStep} steps={STEP_TITLES} />
      </div>

      {/* Step Content */}
      <Card className="p-6 mb-6 min-h-[500px]">
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-2">{STEP_TITLES[currentStep - 1]}</h2>
          <p className="text-sm text-muted-foreground">{STEP_DESCRIPTIONS[currentStep - 1]}</p>
        </div>
        {renderStep()}
      </Card>

      {/* Navigation - Hide on final step (Review has its own buttons) */}
      {currentStep < TOTAL_STEPS && (
        <div className="flex justify-between">
          <Button variant="outline" onClick={handlePrevious} disabled={currentStep === 1}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Previous
          </Button>
          <Button onClick={handleNext} disabled={!validateStep(currentStep).isValid}>
            Next
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      )}

      {/* Previous button only on final step */}
      {currentStep === TOTAL_STEPS && (
        <div className="flex justify-start">
          <Button variant="outline" onClick={handlePrevious}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Previous
          </Button>
        </div>
      )}
    </div>
  );
};
